'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

import { Label } from '@/components/ui/label/label';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox/combobox';
import { YearPicker } from '@/components/ui/year-picker';
import { useVehicleMakes } from '../../hooks/use-public-lookups';
import { VehicleMakeCombobox } from '../vehicle/VehicleMakeCombobox';
import { REGISTRATION_THEME_CLASSES, type RegistrationTheme } from './theme';
import { VehicleModelCombobox } from './VehicleModelCombobox';

/**
 * Vehicle fields any consuming form must expose. Both the signup and
 * complete-registration forms carry these identically-named string fields.
 */
export interface VehicleFieldsValues {
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
}

interface VehicleFieldsProps<T extends FieldValues> {
  control: Control<T>;
  /** Visual theme — see REGISTRATION_THEME_CLASSES. */
  theme: RegistrationTheme;
  vehicleType: 'motorcycle' | 'car';
  /** Prefix for DOM ids so the two call sites stay unique (e.g. 'signup', 'complete'). */
  idPrefix: string;
}

/**
 * Shared Year / Make / Model grid for the registration vehicle forms.
 *
 * - Make is a {@link VehicleMakeCombobox} for car tenants (DGD) — catalog
 *   suggestions plus free-typed custom entry, because the car make long tail is
 *   far wider than the curated list. Motorcycle tenants (DGR/UWR) keep the
 *   curated combobox (catalog makes, "Other" last) so their make data stays
 *   reportable.
 * - Model is a {@link VehicleModelCombobox}: catalog suggestions for the chosen
 *   make, with free-typed custom entry always allowed.
 * - Changing Make clears Model (the old model belonged to a different make).
 *
 * Generic over the form type so it serves both `SignupFormData` and
 * `CompleteRegistrationFormData`; both expose {@link VehicleFieldsValues}.
 */
export function VehicleFields<T extends FieldValues>({
  control,
  theme,
  vehicleType,
  idPrefix,
}: VehicleFieldsProps<T>) {
  const c = REGISTRATION_THEME_CLASSES[theme];
  const tAuth = useTranslations('auth');
  const isCar = vehicleType === 'car';

  const vehicleMakePlaceholder = isCar
    ? tAuth('vehicleMakePlaceholderDgd')
    : tAuth('vehicleMakePlaceholder');
  const vehicleModelPlaceholder = isCar
    ? tAuth('vehicleModelPlaceholderDgd')
    : tAuth('vehicleModelPlaceholder');
  // The drawer hides the field label on mobile, so the search placeholder is the
  // only context — spell out "car make"/"car model" for car tenants.
  const vehicleMakeSearchPlaceholder = isCar
    ? tAuth('vehicleMakeSearchPlaceholderDgd')
    : tAuth('vehicleMakeSearchPlaceholder');
  const vehicleModelSearchPlaceholder = isCar
    ? tAuth('vehicleModelSearchPlaceholderDgd')
    : tAuth('vehicleModelSearchPlaceholder');

  // Car tenants render VehicleMakeCombobox, which owns its own catalog query.
  const { data: vehicleMakes = [], isLoading: isLoadingMakes } = useVehicleMakes(
    isCar ? null : vehicleType,
  );
  const makeOptions: ComboboxOption[] = useMemo(
    () => [
      ...vehicleMakes.filter((m) => m !== 'Other').map((make) => ({ value: make, label: make })),
      ...vehicleMakes.filter((m) => m === 'Other').map((make) => ({ value: make, label: make })),
    ],
    [vehicleMakes],
  );

  const year = useController({ control, name: 'vehicleYear' as Path<T> });
  const make = useController({ control, name: 'vehicleMake' as Path<T> });
  const model = useController({ control, name: 'vehicleModel' as Path<T> });

  const makeValue = (make.field.value as string) ?? '';
  const modelValue = (model.field.value as string) ?? '';

  const handleMakeChange = (next: string) => {
    if (next !== makeValue) {
      // Cascade reset — the previously chosen model belonged to a different make.
      model.field.onChange('');
    }
    make.field.onChange(next);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {/* Year */}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-vehicleYear`} className={c.label}>
          {tAuth('vehicleYear')}
        </Label>
        <YearPicker
          value={(year.field.value as string) ?? ''}
          onValueChange={year.field.onChange}
          placeholder={tAuth('vehicleYearPlaceholder')}
          className={c.yearPicker}
          contentClassName={c.pickerContent}
        />
        {year.fieldState.error && (
          <p id={`${idPrefix}-vehicleYear-error`} role="alert" className={c.fieldError}>
            {year.fieldState.error.message}
          </p>
        )}
      </div>

      {/* Make */}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-vehicleMake`} className={c.label}>
          {tAuth('vehicleMake')}
        </Label>
        {isCar ? (
          <VehicleMakeCombobox
            value={makeValue}
            onValueChange={handleMakeChange}
            vehicleType={vehicleType}
            placeholder={vehicleMakePlaceholder}
            searchPlaceholder={vehicleMakeSearchPlaceholder}
            className={c.combobox}
            popoverClassName={c.comboboxPopover}
          />
        ) : (
          <Combobox
            options={makeOptions}
            value={makeValue}
            onValueChange={handleMakeChange}
            placeholder={vehicleMakePlaceholder}
            searchPlaceholder={vehicleMakeSearchPlaceholder}
            disabled={isLoadingMakes}
            className={c.combobox}
            popoverClassName={c.comboboxPopover}
          />
        )}
        {make.fieldState.error && (
          <p id={`${idPrefix}-vehicleMake-error`} role="alert" className={c.fieldError}>
            {make.fieldState.error.message}
          </p>
        )}
      </div>

      {/* Model */}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-vehicleModel`} className={c.label}>
          {tAuth('vehicleModel')}
        </Label>
        <VehicleModelCombobox
          value={modelValue}
          onValueChange={model.field.onChange}
          vehicleType={vehicleType}
          make={makeValue}
          placeholder={vehicleModelPlaceholder}
          searchPlaceholder={vehicleModelSearchPlaceholder}
          className={c.combobox}
          popoverClassName={c.comboboxPopover}
        />
        {model.fieldState.error && (
          <p id={`${idPrefix}-vehicleModel-error`} role="alert" className={c.fieldError}>
            {model.fieldState.error.message}
          </p>
        )}
      </div>
    </div>
  );
}
